/**
 * @mentions in internal notes.
 *
 * The note's text says "@Ayşe Yılmaz" for people to read; the list of whom to
 * notify is worked out from that text against the team at send time, so a
 * mention that was typed and then deleted notifies nobody.
 */

export interface Mentionable {
  id: number;
  displayName: string;
}

/** The "@query" being typed right before the caret, if any. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@([\p{L}\p{N}._-]{0,30})$/u.exec(before);
  if (!match) return null;
  return { start: before.length - match[2]!.length - 1, query: match[2]! };
}

export function filterTeam<T extends Mentionable>(team: T[], query: string, selfId: number): T[] {
  const q = query.toLocaleLowerCase("tr");
  return team
    .filter((m) => m.id !== selfId)
    .filter((m) => !q || m.displayName.toLocaleLowerCase("tr").split(/\s+/).some((part) => part.startsWith(q)) || m.displayName.toLocaleLowerCase("tr").startsWith(q))
    .slice(0, 6);
}

/** Replaces the "@query" at `start` with the full name, and says where the caret goes. */
export function insertMention(text: string, start: number, caret: number, name: string): { text: string; caret: number } {
  const inserted = `@${name} `;
  return { text: text.slice(0, start) + inserted + text.slice(caret), caret: start + inserted.length };
}

/** Everyone whose "@Full Name" appears in the text as written. Longest names first, so "@Ali Can" is not read as "@Ali". */
export function extractMentions(text: string, team: Mentionable[]): number[] {
  const lower = text.toLocaleLowerCase("tr");
  const found: number[] = [];
  let remaining = lower;
  for (const member of [...team].sort((a, b) => b.displayName.length - a.displayName.length)) {
    const needle = `@${member.displayName.toLocaleLowerCase("tr")}`;
    const index = remaining.indexOf(needle);
    if (index < 0) continue;
    const after = remaining.charAt(index + needle.length);
    if (after && /[\p{L}\p{N}]/u.test(after)) continue;
    found.push(member.id);
    remaining = remaining.slice(0, index) + " ".repeat(needle.length) + remaining.slice(index + needle.length);
  }
  return found;
}
